import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { keys } from '../services/queryKeys'
import { getOrders } from '../services/orderService'
import { ORDER_STATUSES } from '../utils/constants'
import OrderStatus, { STATUS_LABELS } from '../components/orders/OrderStatus'
import RouteLine from '../components/common/RouteLine'
import { RowSkeleton, Empty, Failed } from '../components/common/States'
import { coords, dateTime, peso } from '../utils/helpers'

const FILTERS = [
  { value: null, label: 'All' },
  ...ORDER_STATUSES.map((status) => ({ value: status, label: STATUS_LABELS[status] })),
]

const Place = ({ children }) => (
  <span className="block text-body-s text-ink-2 truncate">{children}</span>
)

function OrderRow({ order }) {
  return (
    <Link
      to={`/orders/${order.orderId}`}
      className="block px-1 py-4 border-b border-hairline
                 transition-[background-color,padding] duration-quick ease-out
                 hover:bg-blush hover:px-2.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Tabular figures: this is the number a customer screenshots and
              sends over Messenger, and a column of them should line up. */}
          <p className="text-body font-bold tabular-nums tracking-[0.01em] text-ink">
            {order.orderId}
          </p>
          <p className="text-body-s text-ink-3 truncate">{order.partner.businessName}</p>
        </div>

        <OrderStatus status={order.status} />
      </div>

      <RouteLine
        className="mt-3"
        pickup={<Place>{order.pickupSource}</Place>}
        dropoff={
          <Place>
            <span className="tabular-nums">
              {coords(order.dropoffLatitude, order.dropoffLongitude)}
            </span>
          </Place>
        }
      />

      <div className="flex items-baseline justify-between gap-3 mt-3">
        <span className="text-body-s text-ink-3">{dateTime(order.createdAt)}</span>
        <span className="text-body font-semibold tabular-nums text-ink">
          {peso(order.estimatedTotal)}
        </span>
      </div>
    </Link>
  )
}

export default function Orders() {
  const [status, setStatus] = useState(null)

  const { isPending: loading, data, error } = useQuery({
    queryKey: keys.orders.list({ status }),
    queryFn: () => getOrders({ status }),
  })
  const orders = data?.data ?? []

  return (
    <section>
      <div className="px-5 pt-[18px] pb-2.5">
        <h1 className="font-display font-bold text-display-l text-ink">My orders</h1>
        <p className="mt-1 text-body-s text-ink-3">Everything you have sent out.</p>
      </div>

      <div className="sticky top-0 md:top-16 z-20 px-5 pt-2 pb-2.5 bg-shell border-b border-hairline overflow-x-auto scrollbar-none">
        <div className="flex gap-2 w-max">
          {FILTERS.map((filter) => {
            const active = status === filter.value

            return (
              <button
                key={filter.label}
                type="button"
                onClick={() => setStatus(filter.value)}
                aria-pressed={active}
                className={`min-h-[36px] px-3.5 rounded-chip text-body-s font-semibold whitespace-nowrap
                            border transition-colors duration-quick ease-out ${
                              active
                                ? 'border-brand bg-brand text-on-brand'
                                : 'border-edge text-ink-2 hover:border-brand'
                            }`}
              >
                {filter.label}
              </button>
            )
          })}
        </div>
      </div>

      {loading && (
        <div className="px-5 pt-4">
          <RowSkeleton count={3} />
        </div>
      )}

      {!loading && error && <Failed error={error} />}

      {!loading && !error && orders.length === 0 && (
        <Empty
          title={status ? 'Nothing in this state' : 'You have not ordered yet'}
          note={
            status
              ? 'Try another filter, or place an order.'
              : 'Pick a delivery service and your first order will show up here.'
          }
          action={
            <Link
              to="/home"
              className="text-body-s font-semibold text-brand underline underline-offset-4"
            >
              Browse delivery services
            </Link>
          }
        />
      )}

      {!loading && !error && orders.length > 0 && (
        <ul className="px-5 pt-1 pb-6">
          {orders.map((order) => (
            <li key={order.orderId}>
              <OrderRow order={order} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
